import React, { useMemo, useState } from 'react';
import { apiClient } from '../../lib/api/client';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';

type Props = {
  requirementId: string;
  isOpen: boolean;
  onClose: () => void;
  onInitiated?: (jobId: string, initiatedCount: number) => void;
};

const CHUNK_CONCURRENCY = 5;
const MAX_FILES = 15;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per file

export const BulkUploadResumesModal: React.FC<Props> = ({ requirementId, isOpen, onClose, onInitiated }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { user } = useAuth();

  const accepted = useMemo(() => {
    const validFiles = files.filter(f => 
      ['application/pdf','application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(f.type) || 
      ['.pdf','.docx'].some(ext => f.name.toLowerCase().endsWith(ext))
    );
    
    // Validate file count
    if (validFiles.length > MAX_FILES) {
      setValidationErrors([`Maximum ${MAX_FILES} files allowed. You selected ${validFiles.length} files.`]);
      return [];
    }
    
    // Validate file sizes
    const oversizedFiles = validFiles.filter(f => f.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(f => f.name).join(', ');
      setValidationErrors([`Files exceed 10MB limit: ${fileNames}`]);
      return [];
    }
    
    // Clear validation errors if all checks pass
    setValidationErrors([]);
    return validFiles;
  }, [files]);

  const handleClose = () => {
    setFiles([]);
    setError(null);
    setValidationErrors([]);
    setShowSuccessModal(false);
    setSuccessMessage('');
    onClose();
  };

  if (!isOpen) return null;

  async function handleInitiate() {
    setError(null);
    setIsUploading(true);
    try {
      const meta = accepted.map(f => ({ filename: f.name, size: f.size, contentType: f.type || undefined }));
      const init = await apiClient.bulkResumeInitiate(requirementId, meta, user?.id);
      const { job_id, uploads } = init;

      // Upload all files in parallel with small concurrency cap
      const queue = uploads.map(u => ({ ...u, file: accepted.find(f => f.name === u.filename)! }));
      await parallelLimit(queue, CHUNK_CONCURRENCY, async (item) => uploadToS3(item.url, item.fields, item.file));

      // Confirm each uploaded file immediately
      const confirmItems = uploads.map(u => ({ s3Key: u.s3Key, filename: u.filename, itemId: u.itemId }));
      await apiClient.bulkResumeConfirm(job_id, requirementId, confirmItems);

      onInitiated?.(job_id, uploads.length);
      
      // Show success message with 5-minute wait instruction
      setSuccessMessage(`${uploads.length} resumes have been queued for processing. Please come back after 5 minutes and refresh the page to view the analysis results.`);
      setShowSuccessModal(true);
    } catch (e: any) {
      setError(e?.message || 'Failed to bulk upload');
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Bulk upload resumes</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Select up to {MAX_FILES} resumes (PDF and DOCX only) to initiate processing in parallel. Maximum file size: 10MB per file.
        </p>

        <input
          type="file"
          multiple
          accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
          className="block w-full text-sm text-gray-900 dark:text-gray-100 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-gray-800 dark:file:text-gray-200"
        />

        <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
          Selected: {accepted.length} / {MAX_FILES} files
          {files.length > accepted.length && (
            <span className="text-red-500 ml-2">
              ({files.length - accepted.length} invalid files)
            </span>
          )}
        </div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <div className="text-sm text-red-800 dark:text-red-200 font-medium mb-1">
              Validation Errors:
            </div>
            <ul className="text-xs text-red-700 dark:text-red-300 space-y-1">
              {validationErrors.map((error, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-red-500 mr-2">•</span>
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Upload Error */}
        {error && (
          <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <div className="text-sm text-red-800 dark:text-red-200">
              {error}
            </div>
          </div>
        )}

        {/* File Size Info */}
        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Supported formats: PDF, DOC, DOCX, TXT • Max size: 10MB per file
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <Button variant="outline" onClick={handleClose} disabled={isUploading}>Cancel</Button>
          <Button 
            onClick={handleInitiate} 
            disabled={isUploading || accepted.length === 0 || validationErrors.length > 0}
          >
            {isUploading ? 'Uploading...' : `Initiate (${accepted.length})`}
          </Button>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md border border-gray-200 dark:border-gray-700 p-6 mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/20 mb-4">
                <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                ✅ Bulk Upload Successful!
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {successMessage}
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      ⏰ Processing Time
                    </h4>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                      The system is processing your resumes in the background. Please come back after 5 minutes and refresh the page to view the analysis results.
                    </p>
                  </div>
                </div>
              </div>
              <Button onClick={handleClose} className="w-full">
                Got it!
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

async function uploadToS3(url: string, fields: Record<string, string>, file: File) {
  const form = new FormData();
  Object.entries(fields).forEach(([k, v]) => form.append(k, v));
  form.append('file', file);
  const res = await fetch(url, { method: 'POST', body: form });
  if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`);
}

async function parallelLimit<T>(items: T[], limit: number, worker: (item: T) => Promise<any>) {
  const active: Promise<any>[] = [];
  for (const item of items) {
    const p = worker(item).finally(() => {
      const idx = active.indexOf(p);
      if (idx >= 0) active.splice(idx, 1);
    });
    active.push(p);
    if (active.length >= limit) await Promise.race(active);
  }
  await Promise.all(active);
}

export default BulkUploadResumesModal;


